import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { INVOICE_STATUS_LABELS } from "@/constants/invoices";
import { queryKeys } from "@/constants/queryKeys";
import { useCustomerScopedQuery } from "@/hooks/useCustomerScopedQuery";
import { useToast } from "@/hooks/useToast";
import { services } from "@/services/registry";
import { calculateInvoiceTotals } from "@/utils/formatters";
function updateInvoiceInListCache(existingData, invoiceId, updates) {
    if (!existingData) {
        return existingData;
    }
    return {
        ...existingData,
        invoices: existingData.invoices.map((invoice)=>invoice.invoiceId === invoiceId ? {
                ...invoice,
                ...updates
            } : invoice)
    };
}
function updateInvoiceByIdCache(existingData, invoiceId, updates) {
    if (!existingData || existingData.invoiceId !== invoiceId) {
        return existingData;
    }
    return {
        ...existingData,
        ...updates
    };
}
export function useInvoiceQuery(invoiceId, customerId) {
    return useQuery({
        queryKey: queryKeys.invoices.byId(invoiceId, customerId),
        queryFn: ()=>services.invoicesService.getInvoiceById(invoiceId, customerId),
        enabled: !!invoiceId,
        staleTime: 5 * 60 * 1000
    });
}
export function useInvoices(filters, enabled) {
    const queryClient = useQueryClient();
    const toast = useToast();
    const query = useCustomerScopedQuery({
        queryKey: queryKeys.invoices.list(filters),
        queryFn: ({ customerId })=>services.invoicesService.getInvoices(filters, customerId),
        staleTime: 5 * 60 * 1000,
        enabled
    });
    const updateStatus = useMutation({
        mutationFn: ({ invoiceId, status, customerId, amountPaid })=>services.invoicesService.updateInvoiceStatus(invoiceId, status, customerId, amountPaid),
        onMutate: async ({ invoiceId, status, amountPaid })=>{
            await queryClient.cancelQueries({
                queryKey: [
                    "invoices"
                ]
            });
            await queryClient.cancelQueries({
                queryKey: [
                    "invoice"
                ]
            });
            const listSnapshots = queryClient.getQueriesData({
                queryKey: [
                    "invoices"
                ]
            });
            const byIdSnapshots = queryClient.getQueriesData({
                queryKey: [
                    "invoice"
                ]
            });
            queryClient.setQueriesData({
                queryKey: [
                    "invoices"
                ]
            }, (existingData)=>updateInvoiceInListCache(existingData, invoiceId, {
                    status,
                    ...amountPaid !== undefined ? {
                        amountPaid
                    } : {}
                }));
            queryClient.setQueriesData({
                queryKey: [
                    "invoice"
                ]
            }, (existingData)=>updateInvoiceByIdCache(existingData, invoiceId, {
                    status,
                    ...amountPaid !== undefined ? {
                        amountPaid
                    } : {}
                }));
            return {
                listSnapshots,
                byIdSnapshots
            };
        },
        onSuccess: (updatedInvoice, { invoiceId, status })=>{
            queryClient.setQueriesData({
                queryKey: [
                    "invoices"
                ]
            }, (existingData)=>updateInvoiceInListCache(existingData, invoiceId, updatedInvoice));
            queryClient.setQueriesData({
                queryKey: [
                    "invoice"
                ]
            }, (existingData)=>updateInvoiceByIdCache(existingData, invoiceId, updatedInvoice));
            queryClient.invalidateQueries({
                queryKey: [
                    "invoices"
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "invoice"
                ]
            });
            toast.success(`Invoice marked as ${INVOICE_STATUS_LABELS[status]}`);
        },
        onError: (error, _variables, context)=>{
            context?.listSnapshots?.forEach(([queryKey, data])=>{
                queryClient.setQueryData(queryKey, data);
            });
            context?.byIdSnapshots?.forEach(([queryKey, data])=>{
                queryClient.setQueryData(queryKey, data);
            });
            toast.handleApiError(error, "Failed to update invoice status");
        }
    });
    const download = useMutation({
        mutationFn: async ({ invoiceId, customerId, forceRegenerate })=>{
            const { downloadUrl, fileName } = await services.invoicesService.getDownloadUrl(invoiceId, customerId, forceRegenerate);
            const response = await fetch(downloadUrl);
            if (!response.ok) throw new Error("Download failed");
            const blob = await response.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = fileName || `invoice-${invoiceId}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(link.href);
        },
        onSuccess: ()=>{
            toast.success("Invoice downloaded");
        },
        onError: (error)=>{
            toast.handleApiError(error, "Failed to download invoice");
        }
    });
    const generate = useMutation({
        mutationFn: (request)=>services.invoicesService.generateInvoices(request),
        onSuccess: (result)=>{
            queryClient.invalidateQueries({
                queryKey: [
                    "invoices"
                ]
            });
            queryClient.invalidateQueries({
                queryKey: [
                    "invoice"
                ]
            });
            toast.success(result.invoicesGenerated === 1 ? "Generated 1 invoice" : `Generated ${result.invoicesGenerated} invoices`, {
                description: `${result.periodStart} to ${result.periodEnd}`
            });
        },
        onError: (error)=>{
            toast.handleApiError(error, "Failed to generate invoices");
        }
    });
    const getTotals = ()=>{
        if (!query.data?.invoices) {
            return {
                totalPaid: 0,
                totalPending: 0,
                totalOverdue: 0
            };
        }
        return calculateInvoiceTotals(query.data.invoices);
    };
    const summaryCurrency = query.data?.invoices?.[0]?.currency ?? "USD";
    return {
        invoices: query.data?.invoices || [],
        customerIdentifier: query.data?.customerIdentifier,
        count: query.data?.count || 0,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
        getTotals,
        summaryCurrency,
        updateStatus,
        download,
        generate
    };
}
